import React, { useState } from 'react'
import { Grid, FormControlLabel, Checkbox, Tooltip } from '@material-ui/core'
import { makeStyles, Theme } from '@material-ui/core/styles'
import Table from '@material-ui/core/Table'
import clsx from 'clsx'
import { biTubeConfigContext } from '../../BiTube'
import messages from '../../../../messages'
import { FormattedMessage, useIntl } from 'react-intl'
import { BiTubeProduct, BitubeProperties, DefaultValue } from '../../enum'
import { useFastduplexRuleValidationMutation } from '@src/mutations/ConfiguratorTubesMutation.generated'
import InfoIcon from '@material-ui/icons/Info'
import { ConfiguratorValues } from '../../../../utils/constants'
import styles from '@src/routes/Configurators/utils/styles'
import { getOverhangDisplayValue } from '@src/routes/Configurators/components/OptionalModifications/ModificationResponse'
import { checkValidKey } from '@src/routes/Configurators/utils/util'

export interface SequenceProp {
  baseCount: string
  antisenseBaseCount: string
  molecularWeight: string
  secondaryStructure: string
  primerDimer: string
  meltingTemperature: string
  gcContent: string
  antiMolecularWeight: string
  antiSecondaryStructure: string
  antiPrimerDimer: string
  antiMeltingTemperature: string
  antiGcContent: string
}

const useStyles = makeStyles((theme: Theme) => {
  return {
    contentWrapper: {
      padding: theme.spacing(7, 5),
    },
    subHead: {
      fontSize: theme.typography.pxToRem(16),
      fontWeight: theme.typography.fontWeightMedium,
      margin: theme.spacing(2, 0),
    },
    label: {
      fontWeight: 'bold',
      paddingTop: theme.spacing(2),
    },
    overrideValidation: {
      textAlign: 'right',
      fontWeight: theme.typography.fontWeightMedium,
      paddingLeft: theme.spacing(3),
    },
    over1: {
      fontSize: theme.typography.pxToRem(14),
      color: '#1d5293',
      fontWeight: theme.typography.fontWeightMedium,
    },
    contentTable: {
      margin: theme.spacing(4, 0, 3),
      '& $contentRow': {
        textAlign: 'left',
      },
    },
    seqWrap: {
      paddingLeft: theme.spacing(3.5),
      paddingTop: theme.spacing(2),
      width: theme.spacing(210),
      wordBreak: 'break-all',
    },
    pr20: {
      width: theme.typography.pxToRem(120),
      paddingTop: theme.spacing(5),
    },
    previewNameWrap: {
      width: 115,
      wordBreak: 'break-all',
    },
    sequence: {
      paddingLeft: theme.spacing(10),
      paddingTop: theme.spacing(2),
    },
    sequencePreview: {
      paddingLeft: theme.spacing(3.5),
    },
    sequenceStyle: {
      paddingLeft: theme.spacing(8),
      paddingTop: theme.spacing(2),
    },
    contentRow: {},
    icon: {
      fontSize: theme.typography.pxToRem(16),
      verticalAlign: 'middle',
    },
    InfoIcon: {
      position: 'relative',
      fontSize: theme.typography.pxToRem(16),
      top: theme.spacing(0.5),
      marginLeft: theme.spacing(1.5),
    },
    arrow: {
      color: theme.palette.common.white,
    },
    antisencepreview: {
      paddingRight: theme.spacing(4),
      float: 'left',
    },
    InfoPopup: {
      maxWidth: theme.spacing(65),
      backgroundColor: theme.palette.common.white,
      position: 'relative',
      boxShadow: theme.shadows[4],
      padding: theme.spacing(5),
      fontSize: theme.typography.pxToRem(12),
      color: theme.palette.common.black,
      borderRadius: '2%',
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
    lineHeight: {
      lineHeight: theme.spacing(0.5),
    },
    flex: {
      display: 'flex',
      '& > div': {
        flexBasis: 'fit-content',
      },
    },
    noShrink: {
      flexShrink: 0,
    },
    sequenceItem: {
      width: 12,
    },
    sequenceItemWithPhos: {
      width: 15,
    },
    sequenceItemWithBase: {
      width: 33,
    },
    sequenceItemWithBaseAndPhos: {
      width: 36,
    },
    antiSeqSpacer: {
      width: 18,
    },
    subscript: {
      bottom: theme.spacing(-2.5),
      position: 'relative',
      paddingBottom: 5,
    },
    flexRowContainer: {
      display: 'flex',
      flexDirection: 'row',
      flexWrap: 'wrap',
    },
  }
})

const FastSequenceProperties = (props) => {
  const classes = useStyles()
  const intl = useIntl()
  const GlobalCSS = styles()
  const { productDetails } = props
  const [fastduplexRuleValidation] = useFastduplexRuleValidationMutation()
  const { bitubeState, biTubeDispatch } = React.useContext(biTubeConfigContext)
  const {
    sessionData,
    currentOligoIndex,
    errorResponseJson,
    isSequenceValid,
    isAntiSeqValid,
  } = bitubeState
  const [data, setData] = React.useState<SequenceProp>({
    baseCount: '--',
    antisenseBaseCount: '--',
    molecularWeight: '--',
    secondaryStructure: '--',
    primerDimer: '--',
    meltingTemperature: '--',
    gcContent: '--',
    antiMolecularWeight: '--',
    antiSecondaryStructure: '--',
    antiPrimerDimer: '--',
    antiMeltingTemperature: '--',
    antiGcContent: '--',
  })
  const [overHang, setOverHang] = useState<string | null | undefined>('')
  const [antisenseOverHang, setAntisenseOverHang] = useState<
    string | null | undefined
  >('')
  const [threeMod, setThreeMod] = useState<string | null | undefined>('')
  const [fiveMod, setFiveMod] = useState<string | null | undefined>('')

  const [antisenceThreeMod, setAntisenceThreeMod] = useState<
    string | null | undefined
  >('')
  const [isOverride, setIsOverride] = React.useState<any>({
    checked: false,
  })

  const [antisenceFiveMod, setAntisenceFiveMod] = useState<
    string | null | undefined
  >('')
  const [sequenceVal, setSequenceVal] = useState<string | null | undefined>(
    intl.formatMessage(messages.CONF_SEQUENCE_PREVIEW_HELP_TEXT)
  )
  const [antiSeqVal, setAntiSeqVal] = useState<string | null | undefined>(
    intl.formatMessage(messages.CONF_SEQUENCE_PREVIEW_HELP_TEXT)
  )
  React.useEffect(() => {
    // STRAT-16994
    let invalidSeq = false
    sessionData[currentOligoIndex].listError.forEach((err) => {
      if (
        err.key === BitubeProperties.SequenceKey ||
        err.key === BitubeProperties.AntiSequenceKey
      )
        invalidSeq = true
    })

    if (invalidSeq || !isSequenceValid || !isAntiSeqValid) {
      setSequenceVal('--')
      setAntiSeqVal('--')
      setOverHang('')
      setThreeMod('')
      setFiveMod('')
      setAntisenseOverHang('')
      setAntisenceThreeMod('')
      setAntisenceFiveMod('')
      // STRAT-16994
    } else {
      if (isSequenceValid && sessionData[currentOligoIndex].sequence !== '') {
        setSequenceVal(sessionData[currentOligoIndex].sequence)
        if (
          sessionData[currentOligoIndex].customerOverhang ===
            BitubeProperties.None ||
          sessionData[currentOligoIndex].customerOverhang ===
            DefaultValue.Select
        ) {
          setOverHang('')
        } else {
          setOverHang(
            getOverhangDisplayValue(
              bitubeState.specificationResponse,
              sessionData[currentOligoIndex].customerOverhang
            )
          )
        }
        if (sessionData[currentOligoIndex].threeMod === BitubeProperties.None) {
          setThreeMod('')
        } else {
          setThreeMod(sessionData[currentOligoIndex].threeMod)
        }
        if (sessionData[currentOligoIndex].fiveMod === BitubeProperties.None) {
          setFiveMod('')
        } else {
          setFiveMod(sessionData[currentOligoIndex].fiveMod)
        }
      } else {
        setSequenceVal(
          sessionData[currentOligoIndex].sequence === ''
            ? intl.formatMessage(messages.CONF_SEQUENCE_PREVIEW_HELP_TEXT)
            : '--'
        )
        setOverHang('')
        setThreeMod('')
        setFiveMod('')
      }

      if (
        isAntiSeqValid &&
        sessionData[currentOligoIndex].antisenceSequence?.trim() !== ''
      ) {
        setAntiSeqVal(sessionData[currentOligoIndex].antisenceSequence)
        if (
          sessionData[currentOligoIndex].customerAntisenseOverHang ===
            BitubeProperties.None ||
          sessionData[currentOligoIndex].customerAntisenseOverHang ===
            DefaultValue.Select
        ) {
          setAntisenseOverHang('')
        } else {
          setAntisenseOverHang(
            getOverhangDisplayValue(
              bitubeState.specificationResponse,
              sessionData[currentOligoIndex].customerAntisenseOverHang
            )
          )
        }
        if (
          sessionData[currentOligoIndex].antisenceThreeMod ===
          BitubeProperties.None
        ) {
          setAntisenceThreeMod('')
        } else {
          setAntisenceThreeMod(sessionData[currentOligoIndex].antisenceThreeMod)
        }
        if (
          sessionData[currentOligoIndex].antisenceFiveMod ===
          BitubeProperties.None
        ) {
          setAntisenceFiveMod('')
        } else {
          setAntisenceFiveMod(sessionData[currentOligoIndex].antisenceFiveMod)
        }
      } else {
        setAntiSeqVal(
          sessionData[currentOligoIndex].antisenceSequence?.trim() === ''
            ? intl.formatMessage(messages.CONF_SEQUENCE_PREVIEW_HELP_TEXT)
            : '--'
        )
        setAntisenseOverHang('')
        setAntisenceThreeMod('')
        setAntisenceFiveMod('')
      }
    }
    if (
      isSequenceValid &&
      isAntiSeqValid &&
      sessionData[currentOligoIndex].sequence !== '' &&
      sessionData[currentOligoIndex].antisenceSequence !== ''
    ) {
      setData({
        baseCount: errorResponseJson.baseCount,
        antisenseBaseCount: errorResponseJson.antisenseBaseCount,
        molecularWeight: errorResponseJson.molecularWeight,
        secondaryStructure: errorResponseJson.secondaryStructure
          ? intl.formatMessage(
              checkValidKey(
                messages[errorResponseJson.secondaryStructure],
                errorResponseJson.secondaryStructure
              )
            )
          : '--',
        primerDimer: errorResponseJson.primerDimer,
        meltingTemperature: errorResponseJson.meltingTemperature,
        gcContent: errorResponseJson.gcContent,
        antiMolecularWeight: errorResponseJson.antisenseMolecularWeight,
        antiSecondaryStructure: errorResponseJson.antisenseSecondaryStructure
          ? intl.formatMessage(
              checkValidKey(
                messages[errorResponseJson.antisenseSecondaryStructure],
                errorResponseJson.secondaryStructure
              )
            )
          : '--',
        antiPrimerDimer: errorResponseJson.antisensePrimerDimer,
        antiMeltingTemperature: errorResponseJson.antisenseMeltingTemperature,
        antiGcContent: errorResponseJson.antisenseGcContent,
      })
    } else {
      setData({
        baseCount: '--',
        antisenseBaseCount: '--',
        molecularWeight: '--',
        secondaryStructure: '--',
        primerDimer: '--',
        meltingTemperature: '--',
        gcContent: '--',
        antiMolecularWeight: '--',
        antiSecondaryStructure: '--',
        antiPrimerDimer: '--',
        antiMeltingTemperature: '--',
        antiGcContent: '--',
      })
    }
    setIsOverride({
      ...isOverride,
      checked: sessionData[currentOligoIndex].isOverridden,
    })
    // TODO: need a test before we refactor these dependencies
  }, [errorResponseJson, sessionData, isSequenceValid, isAntiSeqValid])

  const ruleValidation = async (obj) => {
    const response = await fastduplexRuleValidation({
      variables: { input: obj, productInput: productDetails },
    })
    const { data } = response
    if (data && !data.fastduplexRuleValidation.serverError) {
      if (!data.fastduplexRuleValidation.listErrors?.length) {
        sessionData[bitubeState.currentOligoIndex].listError = []
      } else {
        sessionData[bitubeState.currentOligoIndex].listError = []
        data.fastduplexRuleValidation.listErrors.forEach((item) => {
          delete item?.__typename
          sessionData[currentOligoIndex].listError.push({
            key: item?.key,
            value: item?.value,
          })
        })
      }
      biTubeDispatch({
        type: 'UPDATE_INPUT',
        data: [...bitubeState.sessionData],
      })
      biTubeDispatch({
        type: 'UPDATE_ERROR_RESPONSE',
        data: data.fastduplexRuleValidation,
      })
      biTubeDispatch({ type: 'HAS_OVERRIDDEN_FLAG', data: true })
    } else {
      biTubeDispatch({
        type: 'SET_PAGE_ERROR',
        data: ConfiguratorValues.InternalServerError,
      })
      window.scrollTo(0, 0)
    }
  }
  React.useEffect(() => {
    if (bitubeState.overiddenNumber !== 0) {
      ruleValidation(bitubeState.errorData)
    }

    // TODO: need a test before we refactor these dependencies
  }, [sessionData[currentOligoIndex].isOverridden])

  const handleIsOverride = (event) => {
    setIsOverride({ ...isOverride, checked: event.target.checked })

    sessionData[currentOligoIndex].isOverridden = event.target.checked
    if (productDetails.productType === BiTubeProduct.mirna) {
      biTubeDispatch({ type: 'OVERRIDDEN_NUMBER', data: 1 })
    } else {
      if (
        bitubeState.overiddenNumber === 0 &&
        !(
          sessionData[currentOligoIndex].name === '' &&
          sessionData[currentOligoIndex].sequence === '' &&
          sessionData[currentOligoIndex].antisenceName === '' &&
          sessionData[currentOligoIndex].antisenceSequence === '' &&
          sessionData[currentOligoIndex].autoGenerateOligo === false
        )
      ) {
        biTubeDispatch({ type: 'OVERRIDDEN_NUMBER', data: 1 })
      }
    }
    biTubeDispatch({
      type: 'UPDATE_ERROR_DATA',
      data: { ...bitubeState.errorData, isOverridden: event.target.checked },
    })
    biTubeDispatch({
      type: 'UPDATE_INPUT',
      data: [...bitubeState.sessionData],
    })
  }
  React.useEffect(() => {
    if (
      bitubeState.isPageLoad === 2 &&
      sessionData !== null &&
      sessionData.length !== 0 &&
      sessionData[bitubeState.currentOligoIndex] !== null
    ) {
      if (
        Object.keys(sessionData[bitubeState.currentOligoIndex]).length !== 0 &&
        sessionData[currentOligoIndex].isOverridden !== null &&
        sessionData[currentOligoIndex].isOverridden !== undefined
      ) {
        const data: any = sessionData[currentOligoIndex].isOverridden
        setIsOverride({
          ...isOverride,
          checked: data,
        })
      }
      biTubeDispatch({
        type: 'UPDATE_PAGE_LOAD',
        data: 3,
      })
    }
    // TODO: need a test before we refactor these dependencies
  }, [bitubeState.isPageLoad])

  const generatingFirstBreakindex = () => {
    return 3
  }

  const generateAntiSeqArray = () => {
    const seq = antiSeqVal?.split('')
    const seqArray: string[] = []
    const seqWithPattern: string[] = []
    if (seq) {
      for (let i = 0; i < seq?.length; i++) {
        if (!seq[i].trim()) {
          continue
        }
        if (seq[i] === '*') {
          const prev = seqArray[seqArray.length - 1]
          seqArray[seqArray.length - 1] = prev + seq[i]
          continue
        }

        if (seqWithPattern.length) {
          seqWithPattern.push(seq[i])
          if (seq[i] === ']') {
            seqArray.push(seqWithPattern.join(''))
            seqWithPattern.length = 0
          }
        } else {
          if (seq[i] === '[') {
            seqWithPattern.push(seq[i])
          } else {
            seqArray.push(seq[i])
          }
        }
      }
    }
    return seqArray.filter(Boolean)
  }

  const getSequenceType = (index) => {
    const antiSeq = generateAntiSeqArray()
    switch (antiSeq[index]?.trim()?.length) {
      case 1:
        return classes.sequenceItem
      case 2:
        return classes.sequenceItemWithPhos
      case 4:
        return classes.sequenceItemWithBase
      case 5:
        return classes.sequenceItemWithBaseAndPhos
      default:
        break
    }
    return ''
  }

  const sequence = (sequence) => {
    const seq = sequence.split('').reverse().join('')

    const firstIndex = generatingFirstBreakindex()
    const secondPart = seq.slice(firstIndex + 1).split('')
    const bottomString = seq.slice(firstIndex, firstIndex + 1)
    const firstPart = seq.slice(0, firstIndex).split('')
    if (sequence === '--' || sequence.length <= 3) return sequence
    return (
      <>
        {firstPart.map((char, index) => {
          return (
            <div key={`${index}-firstPart`} className={getSequenceType(index)}>
              {char}
            </div>
          )
        })}
        <sub className={classes.subscript}>
          \<sub className={GlobalCSS.subToSubscriot}>{bottomString}</sub>/
        </sub>
        {secondPart.map((char, index) => {
          return (
            <div
              key={`${index}-secondPart`}
              className={getSequenceType(index + firstIndex)}
            >
              {char}
            </div>
          )
        })}
      </>
    )
  }

  const antisenceSequence = (sequence, threemod) => {
    const firstIndex = generatingFirstBreakindex()
    const sequenceArray = generateAntiSeqArray()
    const firstPart = sequenceArray.slice(0, firstIndex)
    const secondPart = sequenceArray.slice(firstIndex)
    if (sequence === '--' || sequenceArray.length <= 3) return sequenceArray
    return (
      <>
        {firstPart.map((item, index) => {
          return (
            <div key={`${index}-firstPart`} className={getSequenceType(index)}>
              {item}
            </div>
          )
        })}
        <div className={classes.antiSeqSpacer}></div>
        {secondPart.map((item, index) => {
          return (
            <div
              key={`${index}-secondPart`}
              className={getSequenceType(index + firstIndex)}
            >
              {item}
            </div>
          )
        })}
        {threemod}
      </>
    )
  }
  const duplexAntiSequenceComponent = () => {
    if (
      sessionData[currentOligoIndex].productType === BiTubeProduct.LightCycler
    ) {
      return (
        <div
          className={
            antiSeqVal === DefaultValue.sequencePreview
              ? clsx(classes.sequencePreview, GlobalCSS.globalHelpTextColor)
              : ''
          }
        >
          {antisenceFiveMod}
          {antiSeqVal}
          {antisenceThreeMod}
        </div>
      )
    } else if (productDetails.productType === BiTubeProduct.mirna) {
      let paddinglength = 0
      const threeModset1 = ['[Phos]', '[ThiC6]', '[Btn]']
      const threeModset2 = ['[Cyanine3]', '[Cyanine5]']
      if (threeMod) {
        if (threeModset1.includes(threeMod)) {
          paddinglength = (threeMod.length - 1) * 2 * 4
        } else if (threeModset2.includes(threeMod)) {
          paddinglength = (threeMod.length - 1.5) * 2 * 4
        } else {
          paddinglength = threeMod.length * 2 * 4
        }
      }
      if (antiSeqVal === DefaultValue.sequencePreview) {
        paddinglength = 14
      } else if (antiSeqVal === '--') {
        paddinglength = 0
      } else {
        paddinglength = 48 + paddinglength
      }

      return (
        <div
          style={{ paddingLeft: paddinglength }}
          className={
            antiSeqVal === DefaultValue.sequencePreview
              ? clsx(
                  classes.sequencePreview,
                  GlobalCSS.globalHelpTextColor,
                  classes.flexRowContainer
                )
              : clsx(classes.flexRowContainer)
          }
        >
          {antiSeqVal === DefaultValue.sequencePreview
            ? antiSeqVal
            : antisenceSequence(antiSeqVal, antisenceThreeMod)}
        </div>
      )
    } else {
      return (
        <div
          className={
            antiSeqVal === DefaultValue.sequencePreview
              ? clsx(classes.sequencePreview, GlobalCSS.globalHelpTextColor)
              : ''
          }
        >
          {antisenceFiveMod}
          {antiSeqVal}
          {antisenseOverHang}
          {antisenceThreeMod}
        </div>
      )
    }
  }
  const duplexSequenceComponent = () => {
    if (productDetails.productType === BiTubeProduct.Duplex) {
      return (
        <div
          className={
            sequenceVal === DefaultValue.sequencePreview
              ? clsx(classes.sequencePreview, GlobalCSS.globalHelpTextColor)
              : ''
          }
        >
          {fiveMod}
          {sequenceVal}
          {overHang}
          {threeMod}
        </div>
      )
    } else if (productDetails.productType === BiTubeProduct.LightCycler) {
      return (
        <div
          className={
            sequenceVal === DefaultValue.sequencePreview
              ? clsx(classes.sequencePreview, GlobalCSS.globalHelpTextColor)
              : ''
          }
        >
          {fiveMod}
          {sequenceVal}
          {threeMod}
        </div>
      )
    } else if (productDetails.productType === BiTubeProduct.mirna) {
      return (
        <div
          className={clsx(
            sequenceVal === DefaultValue.sequencePreview
              ? clsx(classes.sequencePreview, GlobalCSS.globalHelpTextColor)
              : '',
            classes.lineHeight,
            classes.flex
          )}
        >
          <div className={classes.noShrink}>{threeMod}</div>
          <div className={classes.noShrink}>{overHang}</div>
          <div className={classes.flexRowContainer}>
            {sequenceVal === DefaultValue.sequencePreview
              ? sequenceVal
              : sequence(sequenceVal)}
            {fiveMod}
          </div>
        </div>
      )
    } else {
      return (
        <div
          className={
            sequenceVal === DefaultValue.sequencePreview
              ? clsx(classes.sequencePreview, GlobalCSS.globalHelpTextColor)
              : ''
          }
        >
          {sequenceVal}
          {overHang}
        </div>
      )
    }
  }
  const mirnaProperties = () => {
    return (
      <tbody>
        <tr>
          <td>
            <div className={classes.previewNameWrap}>
              {sessionData[currentOligoIndex].antisenceName
                ? sessionData[currentOligoIndex].antisenceName
                : '--'}
            </div>
          </td>
          <td>{data.antisenseBaseCount}</td>
          <td>{data.antiMolecularWeight}</td>
          <td>{data.antiSecondaryStructure}</td>
          <td>{data.antiPrimerDimer}</td>
          <td>{data.antiMeltingTemperature}</td>
          <td>{data.antiGcContent}</td>
        </tr>
        <tr>
          <td>
            <div className={classes.previewNameWrap}>
              {sessionData[currentOligoIndex].name
                ? sessionData[currentOligoIndex].name
                : '--'}
            </div>
          </td>
          <td>{data.baseCount}</td>
          <td>{data.molecularWeight}</td>
          <td>{data.secondaryStructure}</td>
          <td>{data.primerDimer}</td>
          <td>{data.meltingTemperature}</td>
          <td>{data.gcContent}</td>
        </tr>
      </tbody>
    )
  }
  return (
    <div className={classes.contentWrapper}>
      <h4 className={classes.subHead}>
        <FormattedMessage {...messages._PC_SEQUENCE_PREVIEW} />
        <Tooltip
          title={
            <FormattedMessage
              {...messages.CONF_SEQUENCE_PROPERTIES_INFO_TEXT}
            />
          }
          classes={{ tooltip: classes.InfoPopup, arrow: classes.arrow }}
          arrow
          placement="bottom-end"
        >
          <InfoIcon
            aria-label="info"
            className={classes.InfoIcon}
            color="primary"
          ></InfoIcon>
        </Tooltip>
      </h4>
      {productDetails.productType !== BiTubeProduct.LightCycler ? (
        productDetails.productType === BiTubeProduct.mirna ? (
          <div>
            {' '}
            <Grid container>
              <Grid item xs={2} className={classes.label}>
                <FormattedMessage
                  {...messages.CONF_MIRNA_AANTISENCE_PROPERTIES}
                />
              </Grid>
              <Grid item xs={10} className={classes.seqWrap}>
                {duplexAntiSequenceComponent()}
              </Grid>
            </Grid>
            <Grid container>
              <Grid item xs={2} className={classes.label}>
                <FormattedMessage {...messages.CONF_MIRNA_SENCE_PROPERTIES} />
              </Grid>
              <Grid item xs={10} className={classes.seqWrap}>
                {duplexSequenceComponent()}
              </Grid>
            </Grid>
          </div>
        ) : (
          <div>
            {' '}
            <Grid container>
              <Grid item xs={3} sm={1} className={classes.label}>
                <FormattedMessage {...messages.CONF_SENSE_PROP} />
              </Grid>
              <Grid item xs={9} className={classes.seqWrap}>
                {duplexSequenceComponent()}
              </Grid>
            </Grid>
            <Grid container>
              <Grid item xs={3} sm={1} className={classes.label}>
                <FormattedMessage {...messages.CONF_ANTISENSE_PROP} />
              </Grid>
              <Grid item xs={9} className={classes.seqWrap}>
                {duplexAntiSequenceComponent()}
              </Grid>
            </Grid>
          </div>
        )
      ) : (
        <div>
          <Grid container>
            <Grid item xs={3} sm={1} className={classes.label}>
              <FormattedMessage {...messages.CONF_DONAR_lightcycler} />
            </Grid>
            <Grid item xs={10} className={classes.seqWrap}>
              {duplexSequenceComponent()}
            </Grid>
          </Grid>

          <Grid container>
            <Grid item xs={3} sm={1} className={classes.label}>
              <FormattedMessage {...messages.CONF_ACCEPTOR_lightcycler} />
            </Grid>
            <Grid item xs={10} className={classes.seqWrap}>
              {duplexAntiSequenceComponent()}
            </Grid>
          </Grid>
        </div>
      )}
      <Table className={classes.contentTable}>
        <thead>
          <tr>
            {productDetails.productType !== BiTubeProduct.LightCycler ? (
              productDetails.productType === BiTubeProduct.mirna ? (
                <th className={clsx(classes.contentRow)}>
                  <FormattedMessage {...messages.NAME} />
                </th>
              ) : (
                <th className={clsx(classes.contentRow, classes.pr20)}>
                  <FormattedMessage {...messages.CONF_DUPLEX_PROP_NAME} />
                </th>
              )
            ) : (
              <th className={clsx(classes.contentRow)}>
                <FormattedMessage {...messages.CONF_OLIGO_NAME_standard} />
              </th>
            )}
            <th className={classes.contentRow}>
              <FormattedMessage {...messages.CONF_BASES} />
            </th>
            <th className={classes.contentRow}>
              <FormattedMessage {...messages.CONF_MOLECULAR_WEIGHT} />
            </th>
            <th className={classes.contentRow}>
              <FormattedMessage {...messages.CONF_SECONDARY_STRUCTURE} />
            </th>
            <th className={classes.contentRow}>
              <FormattedMessage {...messages.CONF_PRIMER_DIMER} />
            </th>
            <th className={classes.contentRow}>
              <FormattedMessage {...messages.CONF_MELTING_TEMP} />
            </th>
            <th className={classes.contentRow}>
              <FormattedMessage {...messages.CONF_GC_CONTENT} />
            </th>
          </tr>
        </thead>
        {productDetails.productType === BiTubeProduct.mirna ? (
          mirnaProperties()
        ) : (
          <tbody>
            <tr>
              <td>
                <div className={classes.previewNameWrap}>
                  {sessionData[currentOligoIndex].name
                    ? sessionData[currentOligoIndex].name
                    : '--'}
                </div>
              </td>
              <td>{data.baseCount}</td>
              <td>{data.molecularWeight}</td>
              <td>{data.secondaryStructure}</td>
              <td>{data.primerDimer}</td>
              <td>{data.meltingTemperature}</td>
              <td>{data.gcContent}</td>
            </tr>
            <tr>
              <td>
                <div className={classes.previewNameWrap}>
                  {sessionData[currentOligoIndex].antisenceName
                    ? sessionData[currentOligoIndex].antisenceName
                    : '--'}
                </div>
              </td>
              <td>{data.antisenseBaseCount}</td>
              <td>{data.antiMolecularWeight}</td>
              <td>{data.antiSecondaryStructure}</td>
              <td>{data.antiPrimerDimer}</td>
              <td>{data.antiMeltingTemperature}</td>
              <td>{data.antiGcContent}</td>
            </tr>
          </tbody>
        )}
      </Table>

      <Grid container>
        <Grid item sm={6}></Grid>

        {productDetails.isCSRSupervisor ? (
          <Grid item sm={6}>
            <Grid className={classes.overrideValidation}>
              <FormControlLabel
                classes={{ label: classes.over1 }}
                control={
                  <Checkbox
                    color="primary"
                    value={intl.formatMessage(
                      messages.CONF_OVERRIDE_VALIDATION
                    )}
                  />
                }
                label={intl.formatMessage(messages.CONF_OVERRIDE_VALIDATION)}
                onChange={handleIsOverride}
                checked={isOverride.checked}
              />
            </Grid>
          </Grid>
        ) : (
          ''
        )}
      </Grid>
    </div>
  )
}

export default FastSequenceProperties
